/**
 * Playground model catalog hook.
 *
 * Fetches ``GET /v1/playground/available-models`` so the playground UI
 * can:
 *
 *   1. Show the "connect a provider" empty state when the tenant has
 *      no active credential (``has_active === false``).
 *   2. Populate the model selector with dispatchable strings only —
 *      providers without an active credential are hidden.
 *   3. Re-check after the user clicks the in-stream error CTA so the
 *      composer unlocks without a full page reload.
 *
 * No external state library — plain ``useState`` + ``useEffect``,
 * same as the rest of the playground hooks.
 */

import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_URL } from "@/api/client";
import { COOKIE_KEYS } from "@/lib/constants";

export interface CatalogProvider {
  provider: string;
  has_active: boolean;
  models: string[];
}

/** One model in the dynamic, priced catalog. IDR figures are estimates
 *  at the current FX; null when no real pricing exists. */
export interface CatalogModelEntry {
  model: string;
  provider: string;
  has_pricing: boolean;
  input_per_1m_usd: number | null;
  output_per_1m_usd: number | null;
  input_per_1m_idr: number | null;
  output_per_1m_idr: number | null;
  fx_rate_used: number | null;
  pricing_source: string | null;
}

export interface CatalogResponse {
  providers: CatalogProvider[];
  models: CatalogModelEntry[];
  fx_rate: number | null;
  has_active: boolean;
}

export interface UsePlaygroundCatalog {
  catalog: CatalogResponse | null;
  loading: boolean;
  error: string | null;
  /** Models the FE may dispatch — flattened from active providers only. */
  activeModels: string[];
  /** Priced catalog entries (the selector's primary source). */
  models: CatalogModelEntry[];
  /** Refetch on demand (after the user connects a provider). */
  refetch(): void;
}

// API_URL is empty in dev (Vite proxy rewrites) and the gateway origin
// in staging / production. The bare relative path resolved to the
// static-asset origin in prod and nginx returned 405.
const ENDPOINT = `${API_URL}/v1/playground/available-models`;

async function fetchCatalog(): Promise<CatalogResponse> {
  const token = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const response = await fetch(ENDPOINT, { method: "GET", headers });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return (await response.json()) as CatalogResponse;
}

export function usePlaygroundCatalog(): UsePlaygroundCatalog {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchCatalog()
      .then((next) => {
        setCatalog(next);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeModels = catalog
    ? catalog.providers.filter((p) => p.has_active).flatMap((p) => p.models)
    : [];
  const models = catalog?.models ?? [];

  return {
    catalog,
    loading,
    error,
    activeModels,
    models,
    refetch: load,
  };
}
